<template>
  <div
    class="slider-box"
    :style="containerHeightStyle"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
    @mousedown="handleMouseDown"
    @touchstart.passive="handleTouchStart"
    @touchmove.passive="handleTouchMove"
    @touchend.passive="handleTouchEnd"
  >
    <div
      ref="track"
      class="slider__track"
      :class="trackClasses"
      :style="trackStyles"
    >
      <slot></slot>
    </div>

    <slider-controls
      v-if="showSliderControls && slidesCount > 1"
      :class="sliderControlsClass"
      :orientation="orientation"
      :disable-prev="!infiniteLoop && currentIndex === 0"
      :disable-next="!infiniteLoop && currentIndex === slidesCount - 1"
      @prev="goToPrev"
      @next="goToNext"
    />

    <slider-pagination
      v-if="showSliderPagination && slidesCount > 1"
      :class="sliderPaginationClass"
      :slides-count="slidesCount"
      :current-index="currentIndex"
      :orientation="orientation"
      @slide-change="goToSlide"
    />
  </div>
</template>

<script>
import SliderControls from './slider-controls';
import SliderPagination from './slider-pagination';
import { RandomId } from '@/shared/lib/random';
import { scrollPositionRestoration } from '@/shared/lib/scroll';
import { SLIDER_EVENTS } from '@/shared/ui/slider';

const { generateRandomId } = RandomId();

export default {
  props: {
    id: {
      type: String,
      default: null,
    },
    orientation: {
      type: String,
      default: 'horizontal',
      validator: (value) => ['horizontal', 'vertical'].includes(value),
    },
    autoplay: {
      type: Boolean,
      default: false,
    },
    autoplayInterval: {
      type: Number,
      default: 3000,
      validator: (value) => value > 0,
    },
    swipeEnabled: {
      type: Boolean,
      default: true,
    },
    animationDuration: {
      type: Number,
      default: 500,
      validator: (value) => value >= 0,
    },
    minSwipeDistance: {
      type: Number,
      default: 50,
    },
    infiniteLoop: {
      type: Boolean,
      default: false,
    },
    sliderControlsClass: {
      type: String,
      default: '',
    },
    showSliderControls: {
      type: Boolean,
      default: true,
    },
    sliderPaginationClass: {
      type: String,
      default: '',
    },
    showSliderPagination: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      currentIndex: 0,
      slidesCount: 0,
      isAnimating: false,
      autoplayTimer: null,
      touchStartPosition: { x: 0, y: 0 },
      isSwiping: false,
      containerHeight: 0,
      isMouseOver: false,
      resizeObserver: null,
      verticalOffset: 0,
    };
  },
  mixins: [ scrollPositionRestoration ],
  components: { SliderControls, SliderPagination },
  computed: {
    getSliderId() {
      return this.id || this.generateRandomId('slider-');
    },
    trackClasses() {
      return {
        'slider__track--horizontal': this.isHorizontal,
        'slider__track--vertical': this.isVertical,
      };
    },
    trackStyles() {
      if (this.isVertical) {
        return {
          transform: `translateY(-${this.verticalOffset}px)`,
          transition: this.isAnimating ? `transform ${this.animationDuration}ms ease-in-out` : 'none',
        };
      } else {
        const offset = this.currentIndex * 100;
        return {
          transform: `translateX(-${offset}%)`,
          transition: this.isAnimating ? `transform ${this.animationDuration}ms ease-in-out` : 'none',
        };
      }
    },
    containerHeightStyle() {
      return {
        height: this.isVertical ? `${this.containerHeight}px` : 'auto',
      };
    },
    isHorizontal() {
      return this.orientation === 'horizontal';
    },
    isVertical() {
      return this.orientation === 'vertical';
    },
  },
  mounted() {
    this.$eventBus.$on(SLIDER_EVENTS.UPDATE, this.handleUpdateSliderEvent);
    this.$eventBus.$on(SLIDER_EVENTS.AUTOPLAY, this.handleAutoplayEvent);
    this.$eventBus.$on(SLIDER_EVENTS.NEXT_SLIDE, this.handleGoToNextSlideEvent);
    this.$eventBus.$on(SLIDER_EVENTS.PREV_SLIDE, this.handleGoToPrevSlideEvent);

    this.$nextTick(() => {
      this.initializeSlider();

      this.resizeObserver = new ResizeObserver(this.updateContainerHeight);
      this.observeChildren();

      window.addEventListener('mouseup', this.handleMouseUp);
      window.addEventListener('mousemove', this.handleMouseMove);
    });
  },
  beforeDestroy() {
    this.$eventBus.$off(SLIDER_EVENTS.UPDATE, this.handleUpdateSliderEvent);
    this.$eventBus.$off(SLIDER_EVENTS.AUTOPLAY, this.handleAutoplayEvent);
    this.$eventBus.$off(SLIDER_EVENTS.NEXT_SLIDE, this.handleGoToNextSlideEvent);
    this.$eventBus.$off(SLIDER_EVENTS.PREV_SLIDE, this.handleGoToPrevSlideEvent);

    this.stopAutoplay();

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }

    window.removeEventListener('mouseup', this.handleMouseUp);
    window.removeEventListener('mousemove', this.handleMouseMove);
  },
  methods: {
    generateRandomId,
    observeChildren() {
      if (this.resizeObserver && this.$refs.track) {
        Array.from(this.$refs.track.children).forEach(child => {
          this.resizeObserver.observe(child);
        });
      }
    },
    initializeSlider() {
      this.slidesCount = this.$refs.track?.children.length || 0;
      this.updateContainerHeight();
      this.startAutoplay();
    },
    updateSlides() {
      this.slidesCount = this.$refs.track?.children.length || 0;
      this.saveScrollPosition();
      this.updateContainerHeight();
      this.restoreScrollPosition();

      this.startAutoplay();
    },
    calculateVerticalOffset() {
      if (!this.isVertical) return;

      let offset = 0;
      for (let i = 0; i < this.currentIndex; i++) {
        const slide = this.$refs.track?.children[i];
        if (slide) {
          offset += slide.getBoundingClientRect().height;
        }
      }
      this.verticalOffset = offset;
    },
    updateContainerHeight() {
      if (this.isVertical) {
        this.$nextTick(() => {
          const currentSlide = this.$refs.track?.children[this.currentIndex];

          if (currentSlide) {
            this.containerHeight = currentSlide.offsetHeight;
          }

          this.calculateVerticalOffset();
        });
      }
    },
    goToSlide(index) {
      if (this.isAnimating || this.slidesCount <= 1) {
        return;
      }
      const newIndex = this.normalizeIndex(index);
      if (newIndex === this.currentIndex) {
        return;
      }

      this.resetScrollPositions();
      this.isAnimating = true;
      this.currentIndex = newIndex;

      this.updateContainerHeight();

      const handleTransitionEnd = () => {
        this.isAnimating = false;
        this.$refs.track.removeEventListener('transitionend', handleTransitionEnd);
      };

      this.$refs.track.addEventListener('transitionend', handleTransitionEnd, { once: true });
    },
    goToPrev() {
      this.goToSlide(this.currentIndex - 1);
    },
    goToNext() {
      let nextIndex = this.currentIndex + 1;
      if (this.autoplay && !this.infiniteLoop && nextIndex >= this.slidesCount) {
        nextIndex = 0;
      }
      this.goToSlide(nextIndex);
    },
    normalizeIndex(index) {
      if (this.infiniteLoop) {
        return (index + this.slidesCount) % this.slidesCount;
      } else {
        return Math.max(0, Math.min(this.slidesCount - 1, index));
      }
    },
    startAutoplay() {
      if (
        !this.autoplay ||
        this.slidesCount <= 1 ||
        this.autoplayTimer !== null
      ) {
        return;
      }
      this.autoplayTimer = setInterval(() => {
        if (!this.isMouseOver) {
          this.goToNext();
        }
      }, this.autoplayInterval);
    },
    stopAutoplay() {
      if (this.autoplayTimer) {
        clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    },
    resetScrollPositions() {
      if (this.$refs.track && this.$refs.track.children) {
        this.$refs.track.scrollTop = 0;

        Array.from(this.$refs.track.children).forEach(slide => {
          slide.scrollTop = 0;
        });
      }
    },
    handleMouseEnter() {
      this.isMouseOver = true;
    },
    handleMouseLeave() {
      this.isMouseOver = false;
    },
    handleMouseDown(event) {
      if (event.button !== 0 || !this.swipeEnabled || this.slidesCount <= 1) {
        return;
      }
      this.isSwiping = true;
      this.touchStartPosition = {
        x: event.clientX,
        y: event.clientY,
      };
      this.isAnimating = false;
      event.preventDefault();
    },
    handleMouseUp(event) {
      if (!this.isSwiping) return;
      this.isSwiping = false;
      const touchEndPosition = {
        x: event.clientX,
        y: event.clientY,
      };
      const deltaX = touchEndPosition.x - this.touchStartPosition.x;
      const deltaY = touchEndPosition.y - this.touchStartPosition.y;
      const direction = this.getSwipeDirection(deltaX, deltaY);
      this.handleSwipeNavigation(direction);
    },
    handleTouchStart(event) {
      if (!this.swipeEnabled || this.slidesCount <= 1) return;
      this.isSwiping = true;
      this.touchStartPosition = {
        x: event.touches[0].clientX,
        y: event.touches[0].clientY,
      };
      this.isAnimating = false;
    },
    handleTouchMove(event) {
      if (!this.isSwiping) {
        return;
      }
      if (this.isHorizontal) {
        event.preventDefault();
      }
    },
    handleTouchEnd(event) {
      if (!this.isSwiping) return;
      this.isSwiping = false;
      const touchEndPosition = {
        x: event.changedTouches[0].clientX,
        y: event.changedTouches[0].clientY,
      };
      const deltaX = touchEndPosition.x - this.touchStartPosition.x;
      const deltaY = touchEndPosition.y - this.touchStartPosition.y;
      const direction = this.getSwipeDirection(deltaX, deltaY);
      this.handleSwipeNavigation(direction);
    },
    getSwipeDirection(deltaX, deltaY) {
      if (this.isHorizontal && Math.abs(deltaX) > this.minSwipeDistance) {
        return deltaX > 0 ? 'right' : 'left';
      }
      if (this.isVertical && Math.abs(deltaY) > this.minSwipeDistance) {
        return deltaY > 0 ? 'down' : 'up';
      }
      return null;
    },
    handleSwipeNavigation(direction) {
      if (!direction) return;
      const isPrev = (this.isHorizontal && direction === 'right') || (this.isVertical && direction === 'down');
      const isNext = (this.isHorizontal && direction === 'left') || (this.isVertical && direction === 'up');
      if (isPrev) {
        if (this.infiniteLoop || this.currentIndex > 0) {
          this.goToPrev();
        }
      } else if (isNext) {
        if (this.infiniteLoop || this.currentIndex < this.slidesCount - 1) {
          this.goToNext();
        }
      }
    },
    handleUpdateSliderEvent({ id, delay = 0 }) {
      if (id === this.getSliderId) {
        setTimeout(() => {
          this.updateSlides();
        }, delay);
      }
    },
    handleAutoplayEvent({ id, autoplay }) {
      if (id === this.getSliderId) {
        autoplay ? this.startAutoplay() : this.stopAutoplay();
      }
    },
    handleGoToNextSlideEvent(id) {
      if (id === this.getSliderId) {
        this.goToNext();
      }
    },
    handleGoToPrevSlideEvent(id) {
      if (id === this.getSliderId) {
        this.goToPrev();
      }
    }
  },
  watch: {
    autoplay: {
      immediate: true,
      handler(enabled) {
        enabled ? this.startAutoplay() : this.stopAutoplay();
      },
    },
    currentIndex(newIndex) {
      this.$emit('slide-change', { id: this.getSliderId, newIndex });
    },
    slidesCount() {
      this.startAutoplay();
    },
  },
};
</script>

<style lang="scss" scoped>
.slider-box {
  position: relative;
  overflow: hidden;
  transition: height 0.2s ease-in-out;
  touch-action: pan-y;
  -webkit-overflow-scrolling: touch;
}

.slider__track {
  display: flex;
  align-items: center;
  will-change: transform;

  &--horizontal {
    flex-direction: row;
    overflow: visible;
  }

  &--vertical {
    flex-direction: column;
    overflow: hidden;
  }
}

.slider__track > ::v-deep * {
  flex-shrink: 0;
  width: 100%;
  height: 100%;
}
</style>
